<script lang="ts">
  // Libs
  import { onDestroy, onMount } from "svelte";

  // Stores
  import CartStore from "@retailer/svelte/stores/CartStore";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";

  // Services
  import { DeliveryDateCalculator } from "@shared/services/delivery_date_calculator.js";

  // Components
  import CartHeader from "@retailer/svelte/Cart/Page/CartHeader.svelte";
  import EmptyCart from "@retailer/svelte/Cart/Page/EmptyCart.svelte";
  import CartBody from "@retailer/svelte/Cart/Page/CartBody.svelte";
  import PageTitle from "@shared/svelte/Components/PageTitle.svelte";
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";

  // Types
  import type { CartType, ManagerType } from "@shared/types/Cart";
  import type { SavedProductDict } from "@shared/types/SavedProducts";
  import type { Distributor } from "@shared/types/Distributor";

  // Props
  export let cartService;
  export let openConnectDistributorModal: (d: Distributor) => void;
  export let managers: ManagerType[];
  export let connectSalesRep;
  export let callbacks: any;

  // Data
  let cart: CartType;
  let savedProducts: SavedProductDict;
  let savedProductsCount: number = 0;
  let showLoading: boolean = false;
  let showConnectDistributorModal: boolean = false;
  let highlightErrors: boolean = false;
  let connectSalesRepDistributor = typeof connectSalesRep === "string" ? connectSalesRep : null;

  const unsubscribe = CartStore.subscribe((n: any) => {
    cart = n;
    savedProducts = window["$rootScope"]?.savedProducts;
    showLoading = false;
  });

  onDestroy(() => {
    unsubscribe();
  });

  callbacks.redoSavedProducts = (sp: SavedProductDict) => {
    savedProducts = sp;
    showLoading = false;
  };

  const openConnectDistributorModalWrapper = (selectedDistro: Distributor) => {
    showConnectDistributorModal = true;
    openConnectDistributorModal(selectedDistro);
  };

  // Functions
  const usesIntegratedDates = order => {
    return (
      order.distributor.uses_integrated_delivery_dates &&
      (!order.distributor.is_rolling_out_integrated_orders ||
        (order.distributor.is_rolling_out_integrated_orders &&
          order.retailer.is_part_of_integrated_orders_rollout))
    );
  };

  const prepareIntegratedDeliveryDates = function () {
    let orders = cart?.orders || [];

    for (let order of orders) {
      if (order && order.distributor && usesIntegratedDates(order)) {
        let calc = new DeliveryDateCalculator(order);
        let integratedDate = calc.firstIntegratedDeliveryDate();
        if (integratedDate)
          order.next_integrated_delivery_date = integratedDate.date.format("dddd (M-D-YYYY)");
      }
    }
  };

  // lifecycle
  onMount(() => {
    if (connectSalesRepDistributor) {
      const mappedDistributors = DistributorsStore.mappedDistributors();
      if (mappedDistributors[connectSalesRepDistributor]) {
        openConnectDistributorModalWrapper(mappedDistributors[connectSalesRepDistributor]);
      }
    }
  });

  // Reactives
  $: itemCount = cart?.total_items || 0;
  $: emptyCart = !cart?.orders?.length;
  $: distributorCount =
    cart?.orders?.filter(order => {
      return order.products.find(product => !product.deleted);
    }).length || 0;
  $: {
    cart = cart;
    savedProducts = savedProducts;
    prepareIntegratedDeliveryDates();
    savedProductsCount = window["$rootScope"].savedProductsCount || 0;
  }
  // need this when the saved for later section doesn't appear
  $: applyGrayBackground = savedProductsCount == 0;
</script>

<PageTitle title="Cart | Provi" />
<FizzStyles>
  <div class="fizz-layout-primary" class:fizz-surface-gray={applyGrayBackground}>
    <CartHeader {distributorCount} {itemCount} />
    <main data-grid-area="main">
      {#if emptyCart}
        <EmptyCart {callbacks} {cartService} {savedProducts} bind:savedProductsCount />
      {:else}
        <CartBody
          {callbacks}
          {savedProducts}
          {managers}
          {cartService}
          {cart}
          bind:showLoading
          openConnectDistributorModal={openConnectDistributorModalWrapper}
          bind:highlightErrors
        />
      {/if}
    </main>
  </div>
</FizzStyles>
